import { ApiService } from '@/service/api'
import type {
  KgDebugEntry,
  KgDebugRequest,
  KgDebugResponse,
  KgDebugRun,
  KgDebugWireSpan,
  KgDebugWireTurn,
} from './types'

/**
 * A run spends real model turns (a plan, an answer, up to two refinements, each with its own
 * deadline), so the default request deadline would cut a slow but healthy run short. Three
 * minutes is past the engine's own worst case.
 */
export const KG_DEBUG_TIMEOUT_MS = 180_000

/** Every engine span and model turn is named `native.kg_retrieval.<stage>` (native-stages.ts). */
const STAGE_PREFIX = 'native.kg_retrieval.'
/** The run's own summary span: the page shows the outcome from the response instead. */
const RUN_SPAN = `${STAGE_PREFIX}run`

/**
 * Client for `POST /v1/kg-retrieval/debug`, the endpoint `just kg-ask` drives, through the global
 * ApiService for auth. The endpoint answers 404 unless the web tier runs with
 * `KG_RETRIEVAL_DEBUG=on` (the dev compose files set it).
 */
export class KgDebugApi {
  constructor(private readonly api: ApiService) {}

  async run(body: KgDebugRequest): Promise<KgDebugRun> {
    const res = await this.api.fetchOrError(
      this.api.getUrl('v1/kg-retrieval/debug'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      { timeoutMs: KG_DEBUG_TIMEOUT_MS }
    )
    return toRun(
      body.question ?? body.line ?? '',
      (await res.json()) as KgDebugResponse
    )
  }
}

function stageOf(name: string): string {
  return name.startsWith(STAGE_PREFIX) ? name.slice(STAGE_PREFIX.length) : name
}

function stepOf(span: KgDebugWireSpan): KgDebugEntry {
  let input = span.input
  let leg: string | undefined
  // The tracer folds a split leg into the span's input (`{ leg, ...input }`); lift it back out.
  if (typeof input === 'object' && input !== null && 'leg' in input) {
    const { leg: named, ...rest } = input as Record<string, unknown>
    if (typeof named === 'string') leg = named
    input = rest
  }
  return {
    kind: 'step',
    stage: stageOf(span.stage),
    ...(leg === undefined ? {} : { leg }),
    startedAt: span.startedAt,
    endedAt: span.endedAt,
    status: span.status,
    input,
    output: span.output,
  }
}

function turnOf(turn: KgDebugWireTurn): KgDebugEntry {
  const input = (
    typeof turn.input === 'object' && turn.input !== null ? turn.input : {}
  ) as { system?: unknown; user?: unknown }
  const error = turn.metadata?.error
  return {
    kind: 'model',
    purpose: turn.metadata?.purpose ?? stageOf(turn.name),
    model: turn.model ?? 'unknown',
    startedAt: turn.startedAt,
    endedAt: turn.endedAt,
    input: {
      system: typeof input.system === 'string' ? input.system : '',
      user: typeof input.user === 'string' ? input.user : '',
    },
    ...(typeof turn.output === 'string' ? { output: turn.output } : {}),
    ...(error === undefined ? {} : { error }),
  }
}

/**
 * The response as one timeline. Spans and model turns come back as two lists; each entry is
 * recorded when it ENDS, so ordering by end time puts a model turn before the step that consumed
 * it (the plan turn before validate, the split turn before the split step).
 */
export function toRun(question: string, res: KgDebugResponse): KgDebugRun {
  const entries = [
    ...(res.model_turns ?? []).map(turnOf),
    ...(res.trace ?? []).filter((s) => s.stage !== RUN_SPAN).map(stepOf),
  ]
  // Stable sort: on a tie the model turn (listed first) stays ahead of its step.
  entries.sort((a, b) => a.endedAt - b.endedAt)
  return {
    question,
    header: res.header,
    outcome: res.outcome,
    timeline: entries,
  }
}
